/**
 * Next.js Data Cache tag names for the site's cached `fetch()` calls
 * (`lib/api.server.ts`'s `getMenu`/`getLocationBySlug`/`getStickyCtaBar`,
 * `lib/api.ts`'s `getCityBySlug`). `app/api/internal/revalidate/route.ts`
 * maps an incoming `path` back to these same tags — kept local to the
 * website (not the shared package) since only the website needs to know
 * which of its own fetches back which path. See
 * spec/70-Standards/70.10-Shared-Page-Cache.md.
 */

export function locationCacheTag(slug: string): string {
  return `location:${slug}`;
}

/**
 * Shared tag carried by every `getLocationBySlug` fetch alongside its own
 * `location:{slug}` tag — lets a bulk/full-site purge clear every location's
 * Data Cache entry in one `revalidateTag` call, since there's no way to
 * enumerate (or wildcard-match) every slug from the route handler.
 */
export function locationsCacheTag(): string {
  return 'locations';
}

export function cityCacheTag(slug: string): string {
  return `ort:${slug}`;
}

export function menuCacheTag(slot: string): string {
  return `menu:${slot}`;
}

export function stickyCtaBarCacheTag(): string {
  return 'sticky-cta-bar';
}

/**
 * Tag on the cached `GET /domain-settings` fetch. Cleared by the
 * `domain-settings` revalidate scope, which the backend sends when an editor
 * saves a Domain Setting — see spec 70.11-Domain-Placeholders § Propagation.
 *
 * Not keyed by brand: a deployment serves exactly one brand and only ever
 * fetches its own settings, so there is nothing to disambiguate.
 */
export function domainSettingsCacheTag(): string {
  return 'domain-settings';
}
